<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import { mapActions, mapGetters } from 'vuex';
import EventBus from '@/utils/event-bus';
import { OPERATION } from '@/services/charts-service';
import cardsModel from '@/mixins/cards-model';
import { sortSeries } from '@/utils/sorting';
import { Flags } from '@/plugins/rollout';
import {
  removeSeparator,
  splitStringOnSeparator,
} from '@/utils/formatter';

export default {
  name: 'BaseTimeSeries',
  components: {
    LineChart,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    field: {
      type: String,
      default: null,
    },
    group: {
      type: String,
      default: null,
    },
    filterName: {
      type: String,
      default: null,
    },

  },
  data() {
    return {
      requestStatus: 'loading',
      undefinedLabeL: this.$t('DataManagement.undefined'),
      rawSeries: [],
      pointsParser: 'series',
      filteredValues: [],
      categories: [],
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          areaspline: {
            fillOpacity: 0.05,
            marker: {
              enabled: false,
            },
          },
        },
        xAxis: {},
      },
      isPublicDashboard: false,
    };
  },
  computed: {
    ...mapGetters('featureFlag', [
      'getValue',
    ]),
    series() {
      if (!this.rawSeries && !this.rawSeries.length) {
        return [];
      }

      let series = this.rawSeries;

      if (this.filteredValues.length) {
        series = series?.filter(point => {
          return this.filteredValues.includes(point.name);
        }) || [];
      }

      const organizedSeries = series?.map(serie => {
        const serieName = this.isPublicDashboard ? removeSeparator(serie.name) : serie.name;
        return {
          name: serieName || this.undefinedLabeL,
          data: serie.data.map((val, index) => {
            return [this.categories[index], val || 0];
          }),
        };
      });

      const sortedSeries = sortSeries(organizedSeries);
      return sortedSeries;
    },
  },
  async created() {
    this.getData();
    const publicDashboardInfo = await this.getValue(Flags.publicDashboardInfo);
    const token = splitStringOnSeparator(publicDashboardInfo, '|')[0] || '';

    this.isPublicDashboard = await Boolean(token);
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    orderDataArrayAsc(data) {
      return data.sort();
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        params: {
          ...(this.field ? { fields: [this.field] } : null),
          ...(this.group ? { grouping: [this.group] } : null),
          operators: this.operation,
          datePortion: this.getDatePortion(options),
        },
      })
        .then(({ axes, points }) => {
          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }

          if (!points) {
            return;
          }

          if (this.$attrs.filter) {
            this.filteredValues = this.$attrs.filter[this.filterName]?.value || [];
          }

          const categories = (axes || [])[0]?.values;

          this.categories = this.formattedCategories(categories);

          this.rawSeries = points;
        })
        .catch(() => {
          this.requestStatus = 'error';
        });
    },
    formattedCategories(categories) {
      const rawCategories = this.orderDataArrayAsc([...categories]);

      return this.formattedCategoriesForQuarter(rawCategories);
    },
  },
};
</script>

